import React from 'react';
import { Task } from '../types/task';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Grid } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

interface TaskAnalyticsProps {
  tasks: Task[];
}

export function TaskAnalytics({ tasks }: TaskAnalyticsProps) {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(task => task.status === 'completed').length;
  const inProgressTasks = tasks.filter(task => task.status === 'in_progress').length;
  const todoTasks = tasks.filter(task => task.status === 'todo').length;

  const highPriorityTasks = tasks.filter(task => task.priority === 'high').length;
  const mediumPriorityTasks = tasks.filter(task => task.priority === 'medium').length;
  const lowPriorityTasks = tasks.filter(task => task.priority === 'low').length;

  // Calculate completion rate for the last 7 days
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), i);
    const start = startOfDay(date);
    const end = endOfDay(date);
    const completedOnDay = tasks.filter(task => 
      task.status === 'completed' && 
      task.completed_at && 
      new Date(task.completed_at) >= start && 
      new Date(task.completed_at) <= end
    ).length;
    return { date, completed: completedOnDay };
  }).reverse();

  const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Task Analytics
      </Typography>
      
      <Grid container spacing={3}>
        {/* Task Status Overview */}
        <Grid component="div" size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Task Status Overview
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Completion Rate
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={completionRate} 
                  sx={{ height: 10, borderRadius: 5, mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {completionRate.toFixed(1)}%
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid component="div" size={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Tasks
                  </Typography>
                  <Typography variant="h6">
                    {totalTasks}
                  </Typography>
                </Grid>
                <Grid component="div" size={6}>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {completedTasks}
                  </Typography>
                </Grid>
                <Grid component="div" size={6}>
                  <Typography variant="body2" color="text.secondary">
                    In Progress
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    {inProgressTasks}
                  </Typography>
                </Grid>
                <Grid component="div" size={6}>
                  <Typography variant="body2" color="text.secondary">
                    To Do
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {todoTasks}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Priority Distribution */}
        <Grid component="div" size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Priority Distribution
              </Typography>
              <Grid container spacing={2}>
                <Grid component="div" size={4}>
                  <Typography variant="body2" color="text.secondary">
                    High Priority
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {highPriorityTasks}
                  </Typography>
                </Grid>
                <Grid component="div" size={4}>
                  <Typography variant="body2" color="text.secondary">
                    Medium Priority
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {mediumPriorityTasks}
                  </Typography>
                </Grid>
                <Grid component="div" size={4}>
                  <Typography variant="body2" color="text.secondary">
                    Low Priority
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {lowPriorityTasks}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Last 7 Days Activity */}
        <Grid component="div" size={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Last 7 Days Activity
              </Typography>
              <Grid container spacing={2}>
                {last7Days.map(({ date, completed }) => (
                  <Grid component="div" size={{ xs: 12, sm: 6, md: 3 }} key={date.toISOString()}>
                    <Box textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        {format(date, 'EEE')}
                      </Typography>
                      <Typography variant="h6">
                        {completed}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(date, 'MMM d')}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
} 